import { Injectable } from "../reflection/injectable";
import { ModalService } from "./modal.service";

export type MessageBoxResult = 'ok' | 'cancel';

export interface MessageBoxButton {
    title?: string;
    result: MessageBoxResult;
}

const ICONS: Record<string, string> = {
    'warning': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48">
        <path fill="#8FFFB4" d="M47.4886 36.4612C47.4976 36.5335 47.4976 36.6067 47.4886 36.6791C47.7755 37.3892 47.9461 38.1413 47.9938 38.9064C48.0316 39.79 47.8959 40.6724 47.5945 41.5032C47.2932 42.3341 46.832 43.097 46.2374 43.7483C45.6425 44.4176 44.9214 44.9612 44.116 45.3477C43.3106 45.7342 42.4369 45.9559 41.5456 45.9998H15.7527C15.5148 46.0031 15.2787 45.9584 15.0583 45.8683C14.8379 45.7782 14.6377 45.6445 14.4694 45.4753C14.3012 45.306 14.1684 45.1046 14.0789 44.8828C13.9893 44.661 13.9449 44.4235 13.9481 44.1841C13.9481 43.7025 14.1382 43.2407 14.4767 42.9002C14.8151 42.5597 15.2741 42.3684 15.7527 42.3684H41.5456C42.3593 42.3124 43.119 41.9388 43.6629 41.3274C43.9576 41.0022 44.1823 40.6194 44.3231 40.2028C44.4639 39.7862 44.5176 39.3448 44.481 38.9064C44.4564 38.5848 44.3917 38.2676 44.2885 37.9622L26.8446 7.24C26.4332 6.49182 25.745 5.9373 24.9294 5.69701C24.1139 5.45673 23.2371 5.55013 22.4896 5.95689C21.9608 6.24661 21.5264 6.68374 21.2385 7.2158L3.7705 37.9622C3.61167 38.3458 3.52989 38.7572 3.52989 39.1727C3.52989 39.5882 3.61167 39.9996 3.7705 40.3832C3.92782 40.7761 4.16233 41.133 4.46 41.4325C4.75768 41.732 5.11238 41.968 5.50285 42.1263C5.83252 42.2588 6.18263 42.3326 6.53746 42.3442C7.01402 42.3754 7.45956 42.5929 7.77879 42.9503C8.09802 43.3077 8.26562 43.7766 8.24576 44.2567C8.2211 44.7213 8.02014 45.1586 7.68436 45.4785C7.34858 45.7983 6.90359 45.9763 6.44121 45.9756C5.67286 45.9477 4.9154 45.7838 4.20358 45.4914C2.5448 44.7974 1.22297 43.476 0.522314 41.8115C0.177521 40.9793 0 40.0865 0 39.1848C0 38.2831 0.177521 37.3903 0.522314 36.558V36.3401L18.0384 5.52111C18.6497 4.39726 19.5682 3.47313 20.6851 2.85804C21.4684 2.42143 22.3297 2.14459 23.2195 2.04337C24.1094 1.94215 25.0103 2.01854 25.8708 2.26815C26.7312 2.51777 27.5343 2.93572 28.234 3.49806C28.9337 4.06041 29.5163 4.75611 29.9484 5.54533L47.4886 36.4612ZM24.0295 36.0496C24.5081 36.0496 24.9671 35.8583 25.3055 35.5178C25.6439 35.1773 25.834 34.7155 25.834 34.2339C25.8277 33.7565 25.6348 33.3009 25.2971 32.9656C24.9593 32.6303 24.504 32.4423 24.0295 32.4424C23.5528 32.4486 23.0975 32.6419 22.7604 32.9811C22.4233 33.3203 22.2312 33.7785 22.225 34.2581C22.2313 34.7355 22.4242 35.1911 22.7619 35.5264C23.0997 35.8617 23.555 36.0497 24.0295 36.0496ZM24.0295 15.1566C23.8083 15.1566 23.5893 15.2004 23.385 15.2856C23.1806 15.3708 22.995 15.4956 22.8386 15.653C22.6822 15.8103 22.5581 15.9971 22.4735 16.2028C22.3888 16.4084 22.3453 16.6287 22.3453 16.8513V27.7457C22.3453 28.1951 22.5227 28.6262 22.8386 28.944C23.1544 29.2618 23.5828 29.4404 24.0295 29.4404C24.4762 29.4404 24.9046 29.2618 25.2204 28.944C25.5363 28.6262 25.7137 28.1951 25.7137 27.7457V16.8513C25.7137 16.6287 25.6702 16.4084 25.5855 16.2028C25.5009 15.9971 25.3768 15.8103 25.2204 15.653C25.064 15.4956 24.8784 15.3708 24.674 15.2856C24.4697 15.2004 24.2507 15.1566 24.0295 15.1566Z"/>
    </svg>`,
};

@Injectable({ type: 'singleton' })
export class MessageBoxService {

    public constructor(private _modalService: ModalService) {
    }

    public async showQuestion(title: string, message: string, primary: MessageBoxResult, ...buttons: MessageBoxButton[]): Promise<MessageBoxResult> {
        const promise: Promise<MessageBoxResult> = new Promise<MessageBoxResult>((resolve: (result: MessageBoxResult) => void) => {
            const messageBoxElement: HTMLDivElement = document.createElement('div');
            messageBoxElement.classList.add('u-message-box');
            const modalContentElement: HTMLDivElement = document.createElement('div');
            modalContentElement.classList.add('u-modal-content');
            messageBoxElement.appendChild(modalContentElement);
            const titleElement: HTMLDivElement = document.createElement('div');
            titleElement.classList.add('u-message-box__title');
            titleElement.innerHTML = title;
            modalContentElement.appendChild(titleElement);
            const messageElement: HTMLDivElement = document.createElement('div');
            messageElement.classList.add('u-message-box__message');
            messageElement.id = 'u-message-box__message';
            messageElement.innerHTML = message;
            modalContentElement.appendChild(messageElement);
            const buttonsElement: HTMLDivElement = document.createElement('div');
            buttonsElement.classList.add('u-message-box__buttons');
            buttonsElement.setAttribute('aria-labelledby', 'u-message-box__message');
            modalContentElement.appendChild(buttonsElement);
            buttons.forEach(b => {
                const buttonElement: HTMLButtonElement = document.createElement('button');
                buttonElement.classList.add('u-button', b.result == primary ? 'u-button--primary': 'u-button--secondary');
                buttonElement.innerHTML = b.title || this.getResultTitle(b.result);
                buttonElement.addEventListener('click', () => {
                    this._modalService.close(messageBoxElement);
                    resolve(b.result);
                });
                buttonsElement.appendChild(buttonElement);
                buttonElement.focus();
            });
            document.body.appendChild(messageBoxElement);
            this._modalService.open(messageBoxElement, 'modal');    
        });
        return promise;
    }

    public async showBasicQuestion(title: string, message: string, icon: string): Promise<MessageBoxResult> {
        const promise: Promise<MessageBoxResult> = new Promise<MessageBoxResult>((resolve: (result: MessageBoxResult) => void) => {
            const messageBoxElement: HTMLDivElement = document.createElement('div');
            messageBoxElement.classList.add('u-message-box');
            const modalContentElement: HTMLDivElement = document.createElement('div');
            modalContentElement.classList.add('u-modal-content');
            messageBoxElement.appendChild(modalContentElement);
            const closeButtonElement: HTMLButtonElement = document.createElement('button');
            closeButtonElement.classList.add('u-modal-close-button');
            closeButtonElement.setAttribute('aria-label', USER_LOCALE.close);
            closeButtonElement.innerHTML = '<div class="a-icon a-icon--close" data-key="close"><i class="app-icons-close"></i></div>';
            closeButtonElement.addEventListener('click', () => {
                this._modalService.close(messageBoxElement);
                resolve('cancel');
            });
            modalContentElement.appendChild(closeButtonElement);
            if (ICONS[icon]) {
                const iconElement: HTMLDivElement = document.createElement('div');
                iconElement.classList.add('u-message-box__icon');
                iconElement.innerHTML = ICONS[icon];
                modalContentElement.appendChild(iconElement);
            }
            const titleElement: HTMLDivElement = document.createElement('div');
            titleElement.classList.add('u-message-box__title', 'u-message-box__title--basic');
            titleElement.innerHTML = title;
            modalContentElement.appendChild(titleElement);
            const messageElement: HTMLDivElement = document.createElement('div');
            messageElement.classList.add('u-message-box__message', 'u-message-box__message--basic');
            messageElement.id = 'u-message-box__message';
            messageElement.innerHTML = message;
            modalContentElement.appendChild(messageElement);
            const buttonsElement: HTMLDivElement = document.createElement('div');
            buttonsElement.classList.add('u-message-box__buttons');
            buttonsElement.setAttribute('aria-labelledby', 'u-message-box__message');
            modalContentElement.appendChild(buttonsElement);
            const buttonElement: HTMLButtonElement = document.createElement('button');
            buttonElement.classList.add('u-button', 'u-button--primary');
            buttonElement.innerHTML = USER_LOCALE.ok;
            buttonElement.addEventListener('click', () => {
                this._modalService.close(messageBoxElement);
                resolve('ok');
            });
            buttonsElement.appendChild(buttonElement);
            buttonElement.focus();
            document.body.appendChild(messageBoxElement);
            this._modalService.open(messageBoxElement, 'modal');    
        });
        return promise;
    }

    public async showInfo(title: string, message?: string): Promise<MessageBoxResult> {
        const promise: Promise<MessageBoxResult> = new Promise<MessageBoxResult>((resolve: (result: MessageBoxResult) => void) => {
            const messageBoxElement: HTMLDivElement = document.createElement('div');
            messageBoxElement.classList.add('u-message-box');
            const modalContentElement: HTMLDivElement = document.createElement('div');
            modalContentElement.classList.add('u-modal-content');
            messageBoxElement.appendChild(modalContentElement);
            const titleElement: HTMLDivElement = document.createElement('div');
            titleElement.classList.add('u-message-box__title');
            titleElement.innerHTML = title;
            modalContentElement.appendChild(titleElement);
            if (message) {
                const messageElement: HTMLDivElement = document.createElement('div');
                messageElement.classList.add('u-message-box__message');
                messageElement.innerHTML = message;
                modalContentElement.appendChild(messageElement);
            }
            const buttonsElement: HTMLDivElement = document.createElement('div');
            buttonsElement.classList.add('u-message-box__buttons');
            modalContentElement.appendChild(buttonsElement);
            ['ok' as MessageBoxResult].forEach(b => {
                const buttonElement: HTMLButtonElement = document.createElement('button');
                buttonElement.classList.add('u-button', b == 'ok' ? 'u-button--primary': 'u-button--secondary');
                buttonElement.innerHTML = this.getResultTitle(b);
                buttonElement.addEventListener('click', () => {
                    this._modalService.close(messageBoxElement);
                    resolve(b);
                });
                buttonsElement.appendChild(buttonElement);
            });
            document.body.appendChild(messageBoxElement);
            this._modalService.open(messageBoxElement, 'modal');    
        });
        return promise;
    }

    private getResultTitle(result: MessageBoxResult): string {
        switch (result) {
            case 'ok':
                return USER_LOCALE.ok;
            case 'cancel':
                return USER_LOCALE.cancel;
            default:
                return null;
        }
    }
}